import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { encryptStorage } from "./EncryptStorage";
import AuthProfile from "../features/auth/services/model/AuthProfile";
import { queryClient } from "../App";
import { deleteAuthToken } from "./fetchAuthToken";
import { AUTH_PROFILE_KEY } from "./UserAccessControlUtils";

export const useAuthProfile = (): [
  AuthProfile | undefined,
  (profile: AuthProfile) => void,
  (preserveState?: boolean, isAuth0Logout?: boolean) => void
] => {
  const navigate = useNavigate();
  const { pathname, hash, search } = useLocation();
  let authProfile: AuthProfile | undefined;
  try {
    authProfile = encryptStorage.decrypt(AUTH_PROFILE_KEY);
  } catch (e) {
    encryptStorage.remove(AUTH_PROFILE_KEY);
  }

  const setAuthProfile = (profile: AuthProfile): void => {
    encryptStorage.encrypt(AUTH_PROFILE_KEY, profile);
  };

  const removeAuthProfile = (): void => {
    encryptStorage.remove(AUTH_PROFILE_KEY);
  };

  const getRedirectUrl = useCallback((): string => {
    const hashPart = hash ? `#${hash}` : "";
    const searchPart = search ? `?${search}` : "";
    return `${pathname}${hashPart}${searchPart}`;
  }, [pathname, hash, search]);

  const handleLogout = useCallback(
    async (preserveState = false): Promise<void> => {
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
      removeAuthProfile();
      deleteAuthToken();

      navigate("/login", {
        ...(preserveState && {
          state: {
            redirect: getRedirectUrl(),
            message: "You have been logged out due to inactivity.",
          },
        }),
        replace: true,
      });
    },
    [pathname, hash, search, navigate, queryClient]
  );

  return [authProfile, setAuthProfile, handleLogout];
};
